<template>
  <div class="login-root">
    <div class="login-wrapper">
      <div class="login-card">
        <div class="text-center mb-4">
          <img
            src="/assets/images/flockr_logo.png"
            alt="Flockr"
            class="login-logo"
            @click="$router.push('/')"
          />
          <p class="login-subtitle mt-2">Africa's Livestock Marketplace</p>
        </div>

        <h5 class="text-center fw-bold mb-4 login-title">Welcome Back</h5>

        <div class="mb-3">
          <label class="auth-label">Phone Number</label>
          <div class="input-group">
            <select v-model="countryCode" class="form-select country-select">
              <option v-for="c in countryCodes" :key="c.code" :value="c.code">
                {{ c.flag }} +{{ c.code }}
              </option>
            </select>
            <input
              v-model="form.phone"
              type="tel"
              class="form-control auth-input phone-input"
              placeholder="712 345 678"
              maxlength="15"
              @input="autoFixPhone"
              @keyup.enter="login"
            />
          </div>
        </div>

        <div class="mb-3">
          <div class="d-flex justify-content-between">
            <label class="auth-label">Password</label>
            <a
              href="javascript:void(0)"
              @click="$router.push('/reset-password')"
              class="login-link login-link-sm"
            >
              Forgot Password?
            </a>
          </div>
          <div class="input-group">
            <input
              :type="showPassword ? 'text' : 'password'"
              v-model="form.password"
              class="form-control auth-input"
              placeholder="Enter your password"
              @keyup.enter="login"
            />
            <button
              type="button"
              class="input-group-text password-toggle"
              @click="showPassword = !showPassword"
            >
              <i class="mdi" :class="showPassword ? 'mdi-eye-off' : 'mdi-eye'"></i>
            </button>
          </div>
        </div>

        <div v-if="errorMsg && !showRegisterPrompt" class="alert login-alert-danger py-2 px-3">
          <i class="mdi mdi-alert-circle me-1"></i> {{ errorMsg }}
        </div>

        <div v-if="showRegisterPrompt" class="register-prompt py-3 px-3 mb-2 text-center">
          <i class="mdi mdi-account-question register-prompt-icon"></i>
          <div class="register-prompt-title mt-2">Account Not Found</div>
          <div class="register-prompt-text mt-1">
            No account exists with this phone number. Create one in seconds!
          </div>
          <button class="btn btn-warning w-100 mt-3" @click="goToRegister">
            <i class="mdi mdi-account-plus me-1"></i> Create Account
          </button>
          <button
            type="button"
            class="btn btn-link btn-sm mt-1 register-prompt-link"
            @click="showRegisterPrompt = false; errorMsg = ''"
          >
            Try a different number
          </button>
        </div>

        <button
          v-if="!showRegisterPrompt"
          class="btn btn-warning w-100 mt-2"
          @click="login"
          :disabled="submitting || !form.phone || !form.password"
        >
          <span v-if="submitting">
            <span class="spinner-border spinner-border-sm me-1"></span>
            Signing in...
          </span>
          <span v-else>
            <i class="mdi mdi-login me-1"></i> Sign In
          </span>
        </button>

        <!-- Google Sign-In -->
        <div v-if="googleEnabled" class="login-divider"><span>or</span></div>
        <div v-if="googleEnabled" class="login-google-wrap">
          <div ref="googleBtn"></div>
          <div v-if="googleBusy" class="text-center mt-2" style="color:#A09888;font-size:12px;"><span class="spinner-border spinner-border-sm me-1"></span>Signing in with Google…</div>
          <div v-if="googleError" class="text-center mt-2" style="color:#FF6B6B;font-size:12px;">{{ googleError }}</div>
        </div>

        <div class="text-center mt-4 login-secondary-text">
          Don't have an account?
          <a href="javascript:void(0)" @click="$router.push('/register')" class="login-link fw-semibold">
            Register
          </a>
        </div>

        <div class="text-center mt-3">
          <a href="javascript:void(0)" @click="$router.push('/marketplace')" class="login-muted-link">
            <i class="mdi mdi-storefront-outline me-1"></i> Browse Marketplace
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
:root,
html[data-theme="dark"] {
  --fm-h: #f5f0e8;
  --fm-s: #6b6356;
  --fm-m: #a09888;
  --fm-bg1: #141414;
  --fm-bg2: #0a0a0a;
  --fm-bg3: #1e1e1e;
  --fm-bg4: #0f0f0f;
  --fm-br: #2a2520;
  --fm-input-br: #2a2520;
  --fm-placeholder: #4a4540;
  --fm-alert-bg: #2d1111;
  --fm-alert-br: #5c2020;
  --fm-alert-text: #ff6b6b;
}

html[data-theme="light"] {
  --fm-h: #1a1a1a;
  --fm-s: #777777;
  --fm-m: #555555;
  --fm-bg1: #ffffff;
  --fm-bg2: #f7f5f2;
  --fm-bg3: #f0ebe4;
  --fm-bg4: #ffffff;
  --fm-br: #e8e2da;
  --fm-input-br: #ddd4ca;
  --fm-placeholder: #8d8478;
  --fm-alert-bg: #fff1f1;
  --fm-alert-br: #f1b5b5;
  --fm-alert-text: #c43d3d;
}

html[data-theme="light"] body {
  background: var(--fm-bg2) !important;
  color: var(--fm-h) !important;
}

html[data-theme="dark"] body {
  background: var(--fm-bg2) !important;
  color: var(--fm-h) !important;
}
</style>

<style scoped>
.login-root {
  background: var(--fm-bg2) !important;
  min-height: 100vh;
}

.login-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
}

.login-card {
  background: var(--fm-bg1) !important;
  border: 1px solid var(--fm-br);
  border-radius: 16px;
  padding: 40px 32px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
}

.login-logo {
  height: 44px;
  cursor: pointer;
}

.login-subtitle {
  color: var(--fm-s);
  font-size: 13px;
}

.login-title {
  color: var(--fm-h);
}

.auth-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--fm-m) !important;
  margin-bottom: 6px;
  display: block;
}

.country-select,
.auth-input,
.password-toggle {
  background: var(--fm-bg4) !important;
  border-color: var(--fm-input-br) !important;
  color: var(--fm-h) !important;
}

.country-select {
  font-weight: 600;
  max-width: 110px;
  font-size: 13px;
  padding: 6px 8px;
  color: #bfa164 !important;
}

.phone-input {
  border-left: none !important;
}

.country-select:focus,
.auth-input:focus,
.password-toggle:focus {
  border-color: #bfa164 !important;
  box-shadow: 0 0 0 2px rgba(191, 161, 100, 0.15) !important;
  outline: none;
}

.auth-input {
  border-radius: 8px;
}

.auth-input::placeholder {
  color: var(--fm-placeholder) !important;
}

.password-toggle {
  cursor: pointer;
}

.login-link {
  color: #bfa164;
  text-decoration: none;
}

.login-link-sm {
  font-size: 12px;
}

.login-alert-danger {
  background: var(--fm-alert-bg);
  border: 1px solid var(--fm-alert-br);
  color: var(--fm-alert-text);
  font-size: 13px;
}

.register-prompt {
  background: var(--fm-bg2);
  border: 1px solid #bfa164;
  border-radius: 10px;
}

.register-prompt-icon {
  font-size: 32px;
  color: #bfa164;
}

.register-prompt-title {
  color: var(--fm-h);
  font-size: 14px;
  font-weight: 600;
}

.register-prompt-text {
  color: var(--fm-m);
  font-size: 12px;
}

.register-prompt-link {
  color: var(--fm-s);
  font-size: 11px;
}

.login-secondary-text {
  color: var(--fm-s);
  font-size: 13px;
}

.login-muted-link {
  color: var(--fm-s);
  font-size: 12px;
  text-decoration: none;
}

@media (max-width: 480px) {
  .login-card {
    padding: 28px 20px;
    border-radius: 12px;
  }
}
.login-divider{display:flex;align-items:center;gap:10px;margin:18px 0 12px;color:var(--fm-s,#6B6356);font-size:11px;text-transform:uppercase;letter-spacing:1px}
.login-divider::before,.login-divider::after{content:'';flex:1;height:1px;background:var(--fm-br,#1E1E1E)}
html[data-theme="light"] .login-divider::before,html[data-theme="light"] .login-divider::after{background:#E0D8D0}
.login-google-wrap{display:flex;flex-direction:column;align-items:center}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker } from "@/api";

export default {
  name: "Login",
  data() {
    return {
      form: { phone: "", password: "" },
      showPassword: false,
      submitting: false,
      errorMsg: "",
      showRegisterPrompt: false,
      countryCode: "254",
      countryCodes: [
        { code: "254", name: "Kenya", flag: "🇰🇪" },
        { code: "256", name: "Uganda", flag: "🇺🇬" },
        { code: "255", name: "Tanzania", flag: "🇹🇿" },
        { code: "250", name: "Rwanda", flag: "🇷🇼" },
        { code: "257", name: "Burundi", flag: "🇧🇮" },
        { code: "251", name: "Ethiopia", flag: "🇪🇹" },
        { code: "252", name: "Somalia", flag: "🇸🇴" },
        { code: "211", name: "South Sudan", flag: "🇸🇸" },
        { code: "243", name: "DR Congo", flag: "🇨🇩" },
        { code: "234", name: "Nigeria", flag: "🇳🇬" },
        { code: "233", name: "Ghana", flag: "🇬🇭" },
        { code: "27", name: "South Africa", flag: "🇿🇦" },
        { code: "260", name: "Zambia", flag: "🇿🇲" },
        { code: "263", name: "Zimbabwe", flag: "🇿🇼" },
        { code: "265", name: "Malawi", flag: "🇲🇼" },
        { code: "258", name: "Mozambique", flag: "🇲🇿" },
        { code: "249", name: "Sudan", flag: "🇸🇩" },
        { code: "20", name: "Egypt", flag: "🇪🇬" },
        { code: "212", name: "Morocco", flag: "🇲🇦" },
        { code: "237", name: "Cameroon", flag: "🇨🇲" },
        { code: "44", name: "United Kingdom", flag: "🇬🇧" },
        { code: "1", name: "USA/Canada", flag: "🇺🇸" },
        { code: "91", name: "India", flag: "🇮🇳" },
        { code: "86", name: "China", flag: "🇨🇳" },
        { code: "971", name: "UAE", flag: "🇦🇪" },
        { code: "966", name: "Saudi Arabia", flag: "🇸🇦" },
        { code: "49", name: "Germany", flag: "🇩🇪" },
        { code: "33", name: "France", flag: "🇫🇷" }
      ],
      themeObserver: null,
      googleEnabled: false,
      googleClientId: '',
      googleBusy: false,
      googleError: ''
    };
  },
  mounted() {
    this.applyTheme();
    this.watchTheme();

    var u = sessionStorage.getItem("userId");
    if (u) this.$router.push(this.$route.query.redirect || "/dashboard");

    this.initGoogleSignIn();
  },
  beforeUnmount() {
    if (this.themeObserver) this.themeObserver.disconnect();
    window.removeEventListener("storage", this.applyTheme);
  },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("flockr_theme") || document.documentElement.getAttribute("data-theme") || "dark";
      window.__flockrSetTheme(t);
    },
    watchTheme() {
      var self = this;
      this.themeObserver = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === "attributes" && mutation.attributeName === "data-theme") {
            self.$forceUpdate();
          }
        });
      });
      this.themeObserver.observe(document.documentElement, { attributes: true });
      window.addEventListener("storage", this.applyTheme);
    },
    autoFixPhone() {
      this.showRegisterPrompt = false;
      this.errorMsg = "";
      var p = this.form.phone.replace(/[^0-9]/g, "");
      if (p.startsWith("0")) p = p.substring(1);
      if (p.startsWith(this.countryCode)) p = p.substring(this.countryCode.length);
      this.form.phone = p;
    },
    formatPhone(raw) {
      var p = (raw || "").replace(/[^0-9]/g, "");
      if (p.startsWith("0")) p = p.substring(1);
      if (p.startsWith(this.countryCode)) p = p.substring(this.countryCode.length);
      return p ? this.countryCode + p : "";
    },
    goToRegister() {
      var phone = this.form.phone;
      this.$router.push({ path: "/register", query: { phone: phone } });
    },
    async login() {
      if (!this.form.phone || !this.form.password) return;
      this.submitting = true;
      this.errorMsg = "";
      try {
        var phone = this.formatPhone(this.form.phone);
        var r = await executeJson(
          { function: "loginUser", phone: phone, password: this.form.password, deviceType: "web" },
          flocker
        );
        if (r.data.success) {
          var user = r.data.data.user;
          var roles = r.data.data.roles || [];
          var token = r.data.data.token || "";
          sessionStorage.setItem("userId", user.id);
          sessionStorage.setItem("isLoggedIn", "true");
          sessionStorage.setItem("userName", user.display_name || "");
          sessionStorage.setItem("userPhone", user.phone || "");
          sessionStorage.setItem("userType", user.user_type || "");
          sessionStorage.setItem("userRoles", JSON.stringify(roles));
          var adminModules = r.data.data.adminModules || [];
          sessionStorage.setItem("adminModules", JSON.stringify(adminModules));
          var primaryRole = roles.find(function(r) {
            return r.is_primary == 1 || r.is_primary === "1";
          });
          sessionStorage.setItem("userRole", (primaryRole && primaryRole.role) || user.user_type || "buyer");
          sessionStorage.setItem("authToken", token);
          localStorage.setItem("authToken", token);
          if (user.avatar_url) sessionStorage.setItem("userAvatar", user.avatar_url);
          if (this.$trackEvent) this.$trackEvent('login', { method: 'phone' });
          var redirect = this.$route.query.redirect || "/dashboard";
          this.$router.push(redirect);
        } else {
          var msg = (r.data.message || "").toLowerCase();
          // 3-strikes lockout — server signalled the account must reset password before retrying
          if (r.data.data && r.data.data.passwordResetRequired) {
            this.errorMsg = "";
            this.showRegisterPrompt = false;
            alert(r.data.message || "Too many failed attempts. We'll send you a reset code.");
            this.$router.push({ path: "/reset-password", query: { phone: this.formatPhone(this.form.phone) } });
            return;
          }
          if (
            msg.indexOf("not found") >= 0 ||
            msg.indexOf("no user") >= 0 ||
            msg.indexOf("does not exist") >= 0 ||
            msg.indexOf("no account") >= 0 ||
            msg.indexOf("not registered") >= 0
          ) {
            this.showRegisterPrompt = true;
            this.errorMsg = "";
          } else {
            this.showRegisterPrompt = false;
            this.errorMsg = r.data.message || "Invalid credentials. Please try again.";
          }
        }
      } catch (e) {
        console.error(e);
        this.errorMsg = "Connection error. Please try again.";
      }
      this.submitting = false;
    },

    async initGoogleSignIn() {
      try {
        var r = await executeJson({ function: 'getGoogleClientId' }, flocker);
        if (!(r.data && r.data.success && r.data.data && r.data.data.enabled)) return;
        this.googleClientId = r.data.data.clientId;
        this.googleEnabled = true;
        await this.loadGoogleScript();
        var self = this;
        if (!window.google || !window.google.accounts || !window.google.accounts.id) return;
        window.google.accounts.id.initialize({
          client_id: this.googleClientId,
          callback: function(resp){ self.onGoogleCredential(resp); }
        });
        this.$nextTick(function(){
          if (self.$refs.googleBtn) {
            window.google.accounts.id.renderButton(self.$refs.googleBtn, { theme: 'outline', size: 'large', width: 320, text: 'continue_with', shape: 'pill' });
          }
        });
      } catch (e) {}
    },
    loadGoogleScript() {
      return new Promise(function(resolve){
        if (window.google && window.google.accounts && window.google.accounts.id) return resolve();
        var existing = document.getElementById('google-id-script');
        if (existing) { existing.addEventListener('load', resolve); return; }
        var s = document.createElement('script');
        s.src = 'https://accounts.google.com/gsi/client';
        s.async = true; s.defer = true; s.id = 'google-id-script';
        s.onload = resolve; s.onerror = resolve;
        document.head.appendChild(s);
      });
    },
    async onGoogleCredential(resp) {
      if (!resp || !resp.credential) return;
      this.googleBusy = true; this.googleError = '';
      try {
        var r = await executeJson({ function: 'loginWithGoogle', idToken: resp.credential }, flocker);
        if (r.data && r.data.success && r.data.data && r.data.data.user) {
          var u = r.data.data.user;
          sessionStorage.setItem('userId', u.id);
          sessionStorage.setItem('userName', u.display_name || (u.first_name + ' ' + u.last_name));
          sessionStorage.setItem('userType', u.user_type || 'buyer');
          sessionStorage.setItem('isLoggedIn', '1');
          if (u.avatar_url) sessionStorage.setItem('userAvatar', u.avatar_url);
          if (u.needs_phone == 1) sessionStorage.setItem('flockr_needs_phone', '1');
          window.dispatchEvent(new CustomEvent('flockr-login-success'));
          if (this.$trackEvent) this.$trackEvent('login', { method: 'google' });
          this.$router.push(u.needs_phone == 1 ? '/me' : (this.$route.query.redirect || '/dashboard'));
        } else {
          this.googleError = (r.data && r.data.message) || 'Google sign-in failed';
        }
      } catch (e) {
        this.googleError = 'Network error during Google sign-in';
      }
      this.googleBusy = false;
    }
  }
};
</script>
